/**
 * SubResendPrompt (SUB-AUTO-RESEND) — the ask that appears right after an
 * install the subcontractor already has is moved, anywhere in the office app
 * (the job page, the board, a call's reschedule): "Powerhouse Electric has the
 * old date — send the new one?" One click sends the same work order the job
 * page sends, with an UPDATED banner saying what changed; if the install lost
 * its date, one click tells them it's postponed. Nothing is sent without the
 * click (a dispatcher dragging dates around while planning must not spam the
 * sub) — "Not Now" leaves the warning on the job page.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, ExternalLink, Loader2, Send, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useMemberName } from "@/components/TeamMemberSelect";
import { useJob } from "./jobs";
import { useInstallBank } from "./installSpec";
import { dayText, jobAddressLine, loadWorkOrder, sendToSub, subFor, type SendOutcome } from "./workOrder";
import { useResendPrompts, type ResendPrompt } from "./resendPrompt";

export function SubResendPrompt() {
  const prompts = useResendPrompts((s) => s.prompts);
  if (prompts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[70] flex flex-col items-center gap-2 px-4">
      {prompts.map((p) => <PromptCard key={p.jobId} p={p} />)}
    </div>
  );
}

function PromptCard({ p }: { p: ResendPrompt }) {
  const dismiss = useResendPrompts((s) => s.dismiss);
  const { data: job } = useJob(p.jobId);
  const memberName = useMemberName();
  const bank = useInstallBank();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<SendOutcome | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const postpone = p.state === "unscheduled";
  const title = p.state === "moved"
    ? `${p.subName} has the old install date`
    : p.state === "needs_date"
      ? `${p.subName} was told this install is postponed`
      : `${p.subName} still has this install on ${dayText(p.was)}`;
  const detail = p.state === "moved"
    ? `${p.was ? dayText(p.was) : "No date"}  →  ${dayText(p.now)}`
    : p.state === "needs_date"
      ? `It's scheduled again: ${dayText(p.now)}`
      : "It isn't scheduled any more — tell them it's postponed so nobody shows up.";

  const act = async () => {
    setBusy(true);
    setErr(null);
    try {
      const packet = await loadWorkOrder(p.jobId, memberName, bank);
      const sub = subFor(packet);
      if (!sub?.email) throw new Error("no_email");
      const out = await sendToSub(packet, sub, postpone ? "postponed" : "work_order");
      setDone(out);
      qc.invalidateQueries({ queryKey: ["job_sends", p.jobId] });
      window.setTimeout(() => dismiss(p.jobId), 3500);
    } catch (e) {
      setErr((e as Error)?.message === "no_email" ? "The subcontractor has no email on file — open the job to fix it." : "Couldn't send — open the job and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div role="alertdialog" aria-label={title}
      className="pointer-events-auto w-full max-w-xl rounded-2xl border border-amber-200 bg-white/95 p-4 shadow-e3 backdrop-blur-md">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
          <CalendarClock className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold leading-snug text-slate-900">{title}</p>
          {job && (
            <p className="mt-0.5 truncate text-[14px] text-slate-600">
              {job.customer_name ?? job.custno}{jobAddressLine(job) ? ` · ${jobAddressLine(job)}` : ""}
            </p>
          )}
          <p className="mt-1 text-[14px] font-medium text-slate-700">{detail}</p>
          {done ? (
            <p className="mt-2 flex items-center gap-1.5 text-[14px] font-semibold text-emerald-700">
              <Check className="h-4 w-4" strokeWidth={3} />
              {done === "sent" ? `Sent to ${p.subName}.` : done === "test" ? "Test job — recorded, never sent." : "Recorded as held — messaging is in testing."}
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button size="sm" disabled={busy} onClick={() => void act()}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {postpone ? "Tell Them It's Postponed" : "Send the New Date"}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => { dismiss(p.jobId); navigate(`/jobs/${p.jobId}?tab=scheduled`); }}>
                <ExternalLink className="h-3.5 w-3.5" /> Open the Job
              </Button>
              <Button size="sm" variant="ghost" onClick={() => dismiss(p.jobId)}>Not Now</Button>
            </div>
          )}
          {err && <p className="mt-2 text-[14px] font-medium text-rose-600">{err}</p>}
        </div>
        <button type="button" onClick={() => dismiss(p.jobId)} aria-label="Dismiss"
          className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
